import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logoIcon from '../assets/logo-icon-exact.png';
import SubscribeModal from '../components/SubscribeModal';
import UpgradeButton from '../components/UpgradeButton';
import Footer from '../components/Footer';

function ScoreChart({ scores }) {
  const sorted = [...scores].reverse();
  if (sorted.length === 0) return null;

  const W = 560, H = 176;
  const PAD = { top: 16, right: 28, bottom: 32, left: 44 };
  const iW = W - PAD.left - PAD.right;
  const iH = H - PAD.top - PAD.bottom;

  const px = (i) => PAD.left + (sorted.length > 1 ? i * iW / (sorted.length - 1) : iW / 2);
  const py = (pct) => PAD.top + (1 - pct / 100) * iH;

  const pts = sorted.map((s, i) => ({
    x: px(i), y: py(s.percentage),
    pct: s.percentage,
    date: new Date(s.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
  }));

  const linePath = pts.length > 1
    ? pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
    : null;
  const areaPath = linePath
    ? `${linePath} L${pts[pts.length - 1].x},${PAD.top + iH} L${pts[0].x},${PAD.top + iH} Z`
    : null;

  const labelStep = Math.max(1, Math.ceil(pts.length / 7));

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', minWidth: 260 }}>
        {[0, 25, 50, 75, 100].map(g => (
          <g key={g}>
            <line x1={PAD.left} y1={py(g)} x2={W - PAD.right} y2={py(g)}
              stroke="rgba(201,168,76,0.12)" strokeWidth="1" />
            <text x={PAD.left - 6} y={py(g) + 4}
              textAnchor="end" fontSize="10" fill="rgba(201,168,76,0.5)">{g}%</text>
          </g>
        ))}
        <line x1={PAD.left} y1={py(70)} x2={W - PAD.right} y2={py(70)}
          stroke="rgba(76,175,109,0.45)" strokeWidth="1" strokeDasharray="5,3" />
        <text x={W - PAD.right + 4} y={py(70) + 4}
          fontSize="9" fill="rgba(76,175,109,0.6)">Pass</text>
        {areaPath && <path d={areaPath} fill="rgba(201,168,76,0.07)" />}
        {linePath && (
          <path d={linePath} fill="none" stroke="#C9A84C"
            strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4.5"
            fill="#C9A84C" stroke="#0C1F3F" strokeWidth="2">
            <title>{p.date}: {p.pct}%</title>
          </circle>
        ))}
        {pts.map((p, i) =>
          (i % labelStep === 0 || i === pts.length - 1) ? (
            <text key={i} x={p.x} y={H - 6}
              textAnchor="middle" fontSize="10" fill="rgba(201,168,76,0.5)">{p.date}</text>
          ) : null
        )}
      </svg>
    </div>
  );
}

function Dashboard({ session, darkMode, toggleDark, isSubscribed, hasFullAccess, trialEndsAt }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [scores, setScores]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalOpen, setModalOpen]     = useState(false);
  const [percentile, setPercentile]     = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [topicStats, setTopicStats]   = useState([]);

  useEffect(() => {
    if (!session) return;

    fetchScores();
    fetchTopicStats();
    supabase.rpc('get_user_percentile', { uid: session.user.id })
      .then(({ data, error }) => { if (!error && data !== null) setPercentile(data); });

    const params = new URLSearchParams(location.search);
    if (params.get('welcome') === 'true') {
      setShowWelcome(true);
      const timer = setTimeout(() => setShowWelcome(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [session, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchScores = async () => {
    if (!session) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('quiz_scores')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(20);
    if (!error) setScores(data);
    setLoading(false);
  };

  const fetchTopicStats = async () => {
    const { data, error } = await supabase
      .from('quiz_scores')
      .select('topic, percentage')
      .eq('user_id', session.user.id);
    if (error || !data?.length) return;
    const map = {};
    data.forEach(({ topic, percentage }) => {
      if (!map[topic]) map[topic] = { topic, sum: 0, count: 0 };
      map[topic].sum += percentage;
      map[topic].count += 1;
    });
    const stats = Object.values(map)
      .map(t => ({ topic: t.topic, avg: Math.round(t.sum / t.count), attempts: t.count }))
      .sort((a, b) => a.avg - b.avg);
    setTopicStats(stats);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const avgScore = scores.length
    ? Math.round(scores.reduce((s, r) => s + r.percentage, 0) / scores.length)
    : 0;
  const totalAnswered = scores.reduce((s, r) => s + r.total, 0);

  const dashboardSubhead = !loading
    ? scores.length === 0
      ? "Your stats will live here. Take 5 quizzes to unlock your personalized analysis — we'll show you where to focus your study time."
      : scores.length <= 4
        ? "Good start — keep going. Your personalized stats will unlock after 5 completed quizzes."
        : "Great work so far. We have compiled a deep analysis of your stats and organized them below — use them to focus on the sections that need it most and study smarter, not longer."
    : '';

  const badges = [
    { icon: '🎯', name: 'First Quiz',    earned: scores.length >= 1,
      tooltip: scores.length >= 1 ? '✓ You completed your first quiz.' : '🔒 Complete your first quiz to unlock.' },
    { icon: '💯', name: 'Century Club',  earned: totalAnswered >= 100,
      tooltip: totalAnswered >= 100 ? '✓ You answered 100+ questions.' : `🔒 Answer 100 questions total. You're at ${totalAnswered}.` },
    { icon: '💪', name: 'Dedicated',     earned: scores.length >= 10,
      tooltip: scores.length >= 10 ? '✓ You completed 10+ quizzes.' : `🔒 Complete 10 quizzes. You're at ${scores.length}.` },
    { icon: '🧠', name: 'Sharp Mind',    earned: scores.length > 0 && avgScore >= 70,
      tooltip: scores.length > 0 && avgScore >= 70 ? '✓ Your average score is 70%+.' : '🔒 Reach a 70%+ average score across all your quizzes.' },
    { icon: '⭐', name: 'Perfect Score', earned: scores.some(s => s.percentage === 100),
      tooltip: scores.some(s => s.percentage === 100) ? '✓ You scored 100% on a quiz.' : '🔒 Score 100% on any single quiz.' },
    { icon: '🎓', name: 'Expert',        earned: scores.length > 0 && avgScore >= 80,
      tooltip: scores.length > 0 && avgScore >= 80 ? '✓ Your average score is 80%+.' : '🔒 Reach an 80%+ average score across all your quizzes.' },
  ];

  return (
    <div className="dashboard-page">
      <header>
        <div className="logo" onClick={() => navigate(session ? '/quiz' : '/home')}>
          <div className="logo-icon"><img src={logoIcon} alt="" /></div>
          <div className="logo-text">TradeReady<span>Pro</span></div>
        </div>
        <div className="header-right">
          <Link to={session ? '/quiz' : '/home'} className="dark-toggle">Home</Link>
          <span className="dark-toggle dark-toggle-active">My Scores</span>
          <button className="dark-toggle" onClick={handleSignOut}>Sign Out</button>
          <Link to="/settings" className="dark-toggle" title="Settings">⚙️</Link>
        </div>
      </header>

      <div className="dashboard-content">
        {showWelcome && (
          <div className="welcome-banner">
            <span>🎉 Welcome to TradeReady Pro! You now have full access to all 2,093+ electrical questions. Start studying!</span>
            <button className="welcome-banner-close" onClick={() => setShowWelcome(false)}>✕</button>
          </div>
        )}
        <div className="dashboard-hero">
          <h1>Your Progress</h1>
          {dashboardSubhead && (
            <p className="dashboard-hero-sub">{dashboardSubhead}</p>
          )}
        </div>

        <UpgradeButton
          session={session}
          isSubscribed={isSubscribed}
          hasFullAccess={hasFullAccess}
          trialEndsAt={trialEndsAt}
        />

        {/* ── STATS ─────────────────────────────────────────────────────── */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{scores.length}</div>
            <div className="stat-label-d">Quizzes Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{avgScore}%</div>
            <div className="stat-label-d">Average Score</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{totalAnswered}</div>
            <div className="stat-label-d">Questions Answered</div>
          </div>
        </div>

        {/* ── PERCENTILE RANKING ───────────────────────────────────────── */}
        {percentile !== null && scores.length > 0 && (
          <div className="ranking-card">
            <div className="ranking-icon">🏅</div>
            <div className="ranking-text">
              You rank in the <strong>top {percentile}%</strong> of all TradeReady Pro users
            </div>
            <div className="ranking-pill">Top {percentile}%</div>
          </div>
        )}

        {/* ── WEAK SPOT ANALYTICS ──────────────────────────────────────── */}
        {topicStats.length >= 2 && (
          <div className="chart-card">
            <div className="chart-card-title">Performance by Topic</div>
            <p className="weak-spot-sub">Sorted weakest to strongest — focus your study time on the red and orange sections.</p>
            <div className="weak-spot-list">
              {topicStats.map(({ topic, avg, attempts }) => {
                const color = avg >= 70 ? '#3aaa73' : avg >= 60 ? '#e0a020' : '#e05252';
                return (
                  <div key={topic} className="weak-spot-row">
                    <div className="weak-spot-label">
                      <span className="weak-spot-topic">{topic}</span>
                      <span className="weak-spot-meta">{attempts} quiz{attempts !== 1 ? 'zes' : ''}</span>
                    </div>
                    <div className="weak-spot-bar-wrap">
                      <div className="weak-spot-bar-track">
                        <div className="weak-spot-bar-fill" style={{ width: `${avg}%`, background: color }} />
                        <div className="weak-spot-pass-line" />
                      </div>
                      <span className="weak-spot-pct" style={{ color }}>{avg}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="weak-spot-legend">
              <span style={{ color: '#e05252' }}>■ Below 60%</span>
              <span style={{ color: '#e0a020' }}>■ 60–69%</span>
              <span style={{ color: '#3aaa73' }}>■ 70%+ (Pass)</span>
            </div>
          </div>
        )}

        {/* ── BADGES ───────────────────────────────────────────────────── */}
        <div className="chart-card">
          <div className="chart-card-title">Achievements</div>
          <div className="badges-grid">
            {badges.map((b) => (
              <div key={b.name} className={`badge-item${b.earned ? ' earned' : ' locked'}`}>
                <div className="badge-tooltip">{b.tooltip}</div>
                <div className="badge-icon">
                  <span className="badge-emblem">{b.icon}</span>
                </div>
                <div className="badge-name">{b.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SCORE CHART ───────────────────────────────────────────────── */}
        <div className="chart-card">
          <div className="chart-card-title">Score Progress Over Time</div>
          {!loading && scores.length < 2 ? (
            <div className="chart-empty">
              Complete at least 2 quizzes to see your progress chart.
            </div>
          ) : (
            <ScoreChart scores={scores} />
          )}
        </div>

        {/* ── QUIZ HISTORY ──────────────────────────────────────────────── */}
        <div className="chart-card">
          <div className="chart-card-title">Quiz History</div>
          {!loading && scores.length === 0 ? (
            <div className="chart-empty">No quizzes completed yet. Start quizzing to see your history here.</div>
          ) : (
            <table className="scores-table">
              <thead>
                <tr>
                  <th>Topic</th>
                  <th>Score</th>
                  <th>Result</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {scores.map(s => (
                  <tr key={s.id}>
                    <td>{s.topic}</td>
                    <td>{s.score}/{s.total}</td>
                    <td className={s.percentage >= 70 ? 'pass' : 'fail'}>
                      {s.percentage}% {s.percentage >= 70 ? '✓' : '✗'}
                    </td>
                    <td>{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modalOpen && <SubscribeModal onClose={() => setModalOpen(false)} session={session} />}
      <Footer />
    </div>
  );
}

export default Dashboard;
